import {
  type MeasureUnit,
  type UserUnitSettings,
  getPreferredUnitForUi,
} from '@mfw/common';

/**
 * Puts a configurable into the reader's display unit AND REMEMBERS THE ONE IT
 * CAME FROM.
 *
 * Two halves of one decision, and separating them is what corrupts data. The
 * save (`mapWorkoutExerciseToApi`) converts back with
 * `dbUnit ?? DB_UNIT_MAP[uiUnit]`, and that fallback is a per-DIMENSION default,
 * not the configurable's own unit — `DB_UNIT_MAP[OZ]` is `G`, so a configurable
 * DECLARED in kilograms, displayed in ounces and saved without `dbUnit` is
 * written as grams against a kilogram column: `100` becomes `2834.95` and
 * reopens as `100000.03`. `M` under a `ft` preference does the same through
 * `DB_UNIT_MAP[FT] = CM`.
 *
 * ONE FUNCTION because there were two sites and only one of them recorded it:
 * `mapWorkoutExerciseRoundsFromApi` (the LOAD) did, and
 * `buildExerciseFromCatalog` (an exercise added from the catalog) rewrote the
 * unit and stopped there — so the thousandfold error survived the fix for
 * exactly the rows a user creates by hand. Two expressions of one rule is how
 * they drifted; there is now one.
 *
 * `dbUnit ?? unit`, not `unit`: re-displaying an already-displayed configurable
 * must not overwrite the original with the display unit, which would lose the
 * only record of where the number has to go back to.
 */
export const withPreferredDisplayUnit = <
  T extends { dbUnit?: MeasureUnit; unit?: MeasureUnit },
>(
  configurable: T,
  userUnitSettings?: UserUnitSettings | null,
  // `T &` rather than plain `T`: a caller whose object type does not DECLARE
  // `dbUnit` still gets one back, and the whole point is that the value is
  // readable afterwards.
): T & { dbUnit?: MeasureUnit } => ({
  ...configurable,
  dbUnit: configurable.dbUnit ?? configurable.unit,
  unit: getPreferredUnitForUi(configurable.unit, userUnitSettings),
});
